import { useCallback } from 'react';
import type { GetEventCTAPageInfoFragment } from '../../components/CtaButtons/CtaButtons.graphql.generated';
import { EventPageType, EventRSVPAttendanceStatus } from '@graphql/generated-base';
import { useTranslation } from '@shared/core';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';

type FilteredPages = {
  pagesHidden: GetEventCTAPageInfoFragment[];
  pagesPasswordProtected: GetEventCTAPageInfoFragment[];
};

type ButtonsVisibility = {
  shouldDisplayUnlockButton: Maybe<boolean>;
  shouldDisplayRSVPButton: boolean;
};

type CustomPageWithSlug = GetEventCTAPageInfoFragment & { page: { pageSlug: string } };

const filterPages = (pages?: Maybe<GetEventCTAPageInfoFragment>[]): FilteredPages => {
  const pagesPasswordProtected: GetEventCTAPageInfoFragment[] = [];
  const pagesHidden: GetEventCTAPageInfoFragment[] = [];

  pages?.forEach(page => {
    if (page?.disabled) {
      pagesHidden.push(page);
    } else if (page?.private && page.type !== EventPageType.schedule && page.type !== EventPageType.rsvp) {
      pagesPasswordProtected.push(page);
    }
  });

  return { pagesHidden, pagesPasswordProtected };
};

export const useCtaButtons = () => {
  const { tq } = useTranslation('layoutEngine');
  const { checkIsWelcomePage } = useWelcomePage();
  // overrides rsvp button text via setting in amplitude
  const rsvpCustomLabelFlag = useFeatureValue('rsvpButtonCustomLabel');
  const rsvpButtonCustomLabel = typeof rsvpCustomLabelFlag.payload === 'string' ? rsvpCustomLabelFlag.payload : undefined;

  const getButtonsVisibility = useCallback(
    (
      pages: Maybe<GetEventCTAPageInfoFragment>[],
      welcomePage: Maybe<GetEventCTAPageInfoFragment>,
      schedulePage: Maybe<GetEventCTAPageInfoFragment>,
      rsvpIsEnabled: Maybe<boolean>,
      rsvpIsPasswordProtected: Maybe<boolean>,
      _: Maybe<boolean>,
      hasUnlocked: Maybe<boolean>,
      customPages?: Maybe<Array<CustomPageWithSlug>>,
    ): ButtonsVisibility => {
      const customWelcomePage = customPages?.find(pageInfo => checkIsWelcomePage(pageInfo?.page?.pageSlug, pageInfo?.type).isCustomWelcomePage);

      // Use custom welcome page if it exists, otherwise fall back to native welcome page
      const activeWelcomePage = customWelcomePage || welcomePage;

      const filteredPages = filterPages(pages);
      const isUnlocked = hasUnlocked ?? false;
      const isAnyPagePasswordProtected = filteredPages.pagesPasswordProtected.length > 0;
      const hasBlockedPages = isAnyPagePasswordProtected && !isUnlocked;

      // Everything is hidden
      if (filteredPages.pagesHidden.length === pages.length) {
        return { shouldDisplayUnlockButton: false, shouldDisplayRSVPButton: false };
      }

      // Welcome page is hidden but RSVP varies
      if (activeWelcomePage?.disabled) {
        return {
          shouldDisplayUnlockButton: rsvpIsPasswordProtected ? !isUnlocked : hasBlockedPages,
          shouldDisplayRSVPButton: false,
        };
      }

      // Welcome page is public and Schedule page is password protected
      if (!schedulePage?.disabled && schedulePage?.private) {
        if (rsvpIsEnabled) {
          return {
            shouldDisplayUnlockButton: !isUnlocked,
            shouldDisplayRSVPButton: rsvpIsPasswordProtected ? isUnlocked : true,
          };
        }

        return {
          shouldDisplayUnlockButton: !isUnlocked,
          shouldDisplayRSVPButton: false,
        };
      }

      // Welcome page is public and Schedule page is hidden or public
      if (rsvpIsEnabled) {
        return {
          shouldDisplayUnlockButton: rsvpIsPasswordProtected ? !isUnlocked : hasBlockedPages,
          shouldDisplayRSVPButton: rsvpIsPasswordProtected ? isUnlocked : true,
        };
      }

      return { shouldDisplayUnlockButton: hasBlockedPages, shouldDisplayRSVPButton: false };
    },
    [checkIsWelcomePage],
  );

  const getUnlockButtonText = useCallback(
    (hasUnlocked: Maybe<boolean>, unlockLabel: string | undefined = '') => {
      if (hasUnlocked) {
        return tq`Unlock Your Schedule`;
      }

      return unlockLabel || tq`Unlock Guest Details`;
    },
    [tq],
  );

  const getRSVPButtonText = useCallback(
    (rsvpAttendanceStatus: EventRSVPAttendanceStatus | undefined, rsvpLabel: string | undefined) => {
      // Service labels are the untranslated labels coming from the server
      // Reference: https://github.com/joylifeinc/legacy-joy-monorepo/blob/develop/modules/app-server-api/handlers/gqlResolvers/event/EventResolver.js#L720
      const defaultServiceLabels = ['RSVP Here', 'Unlock Website', 'You Are Attending', 'You Are Not Attending'];

      // rsvpLabel might be one of the service labels or the user inputted label
      // If it is a user inputted label, it should be returned as is
      if (rsvpLabel && !defaultServiceLabels.includes(rsvpLabel)) {
        return rsvpLabel;
      }

      // Use these default labels if the attendance status is set
      switch (rsvpAttendanceStatus) {
        case EventRSVPAttendanceStatus.attending:
          return tq`You are attending`;
        case EventRSVPAttendanceStatus.notAttending:
          return tq`You are not attending`;
        case EventRSVPAttendanceStatus.other:
          return tq`Other`;
        case EventRSVPAttendanceStatus.noAttendanceQuestion:
        case EventRSVPAttendanceStatus.unanswered:
        default:
          return tq`Don't Forget to RSVP`;
      }
    },
    [tq],
  );

  return { getButtonsVisibility, getUnlockButtonText, getRSVPButtonText, rsvpButtonCustomLabel };
};
